import React, { useState, useCallback } from 'react';
import './InputBar.css';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import Text from '@amzn/meridian/text';
import Box from '@amzn/meridian/box';
import sendIcon from '@amzn/meridian-tokens/base/icon/send-small';
import bugIcon from '@amzn/meridian-tokens/base/icon/alert-knockout';
import BugReportModal from '../BugReportModal/BugReportModal';

interface InputBarProps {
  isLoading: boolean;
  onSendMessage: (message: string) => void;
}

const MAX_INPUT_LENGTH = 2000;

const InputBar: React.FC<InputBarProps> = ({ isLoading, onSendMessage }) => {
  const [inputValue, setInputValue] = useState('');
  const [bugModalOpen, setBugModalOpen] = useState(false);
  const [bugSubmitted, setBugSubmitted] = useState(false);

  const remaining = MAX_INPUT_LENGTH - inputValue.length;

  const handleBugSubmitted = useCallback(() => {
    setBugSubmitted(true);
    window.setTimeout(() => setBugSubmitted(false), 4000);
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length <= MAX_INPUT_LENGTH) {
      setInputValue(e.target.value);
    }
  }, []);

  const handleSend = useCallback(() => {
    const trimmedInput = inputValue.trim();
    if (!trimmedInput || isLoading) return;

    onSendMessage(trimmedInput);
    setInputValue('');
  }, [inputValue, isLoading, onSendMessage]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  return (
    <Box className="input-area">
      <Box className="input-container">
        <button
          type="button"
          className="bug-report-button"
          onClick={() => setBugModalOpen(true)}
          aria-label="Report a bug or issue"
          title="Report a bug or issue"
        >
          <Icon tokens={bugIcon} />
        </button>
        <input
          className="message-input"
          placeholder="Message GFT Ordis..."
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
          type="text"
        />
        <Button
          className="send-button"
          onClick={handleSend}
          disabled={!inputValue.trim() || isLoading}
          type="primary"
          circular={true}
          aria-label="Send message"
        >
          {isLoading ? (
            <div className="loading-spinner"></div>
          ) : (
            <Icon tokens={sendIcon} />
          )}
        </Button>
      </Box>
      <Text className="input-footer" type="b100">
        <span style={{ float: 'right', color: remaining < 100 ? '#d13212' : '#687078' }}>
          {remaining}/{MAX_INPUT_LENGTH}
        </span>
        {bugSubmitted ? (
          <span style={{ color: '#037f0c' }}>
            Thanks — your report was submitted.
          </span>
        ) : (
          'GFT Ordis can make mistakes. Consider checking important information.'
        )}
      </Text>
      <BugReportModal
        open={bugModalOpen}
        onClose={() => setBugModalOpen(false)}
        onSubmitted={handleBugSubmitted}
      />
    </Box>
  );
};

export default InputBar;
